import { z } from 'zod';
import { EnvValueType, EnvVisibility, Platform } from '@hangar/shared';
import { str, nstr, nint } from './schemaPrimitives';
import { EnvironmentSummary, UserSummary, Warning } from './schemasCommon';

export const EnvScope = z.enum(['org', 'project', 'app', 'request', 'system']);
export type EnvScope = z.infer<typeof EnvScope>;

export const EnvDefinitionRef = z.object({ scope: EnvScope.catch('project'), envVarId: nstr, versionId: nstr, version: nint, environment: nstr, platform: nstr, valueFingerprint: nstr });
export const ResolvedEnvEntry = z.object({
  key: str,
  scope: EnvScope.catch('app'),
  source: z.enum(['hangar', 'override', 'system']).catch('hangar'),
  environment: nstr,
  platform: nstr,
  visibility: EnvVisibility,
  valueType: EnvValueType.catch('string'),
  envVarId: nstr,
  versionId: nstr,
  version: nint,
  preview: nstr,
  valueFingerprint: str,
  overriddenBy: z.array(EnvDefinitionRef).default([]),
});
export type ResolvedEnvEntry = z.infer<typeof ResolvedEnvEntry>;
export const MissingEnvRequirement = z.object({ key: str, reason: z.enum(['missing', 'pattern_mismatch', 'visibility_mismatch']).catch('missing'), pattern: nstr, expectedVisibility: nstr, description: nstr });
export type MissingEnvRequirement = z.infer<typeof MissingEnvRequirement>;

export const EnvSnapshot = z.object({
  id: str,
  buildId: str,
  appId: str,
  environment: EnvironmentSummary,
  platform: Platform,
  contentHash: str,
  entries: z.array(ResolvedEnvEntry),
  overrides: z.array(z.object({ key: str, visibility: EnvVisibility, valueFingerprint: str })).default([]),
  createdAt: str,
});
export type EnvSnapshot = z.infer<typeof EnvSnapshot>;

export const EnvResolution = z.object({
  appId: str,
  environment: EnvironmentSummary,
  platform: Platform,
  contentHash: str,
  entries: z.array(ResolvedEnvEntry),
  missing: z.array(MissingEnvRequirement),
  warnings: z.array(Warning),
});
export type EnvResolution = z.infer<typeof EnvResolution>;

export const EnvVarVersion = z.object({
  id: str,
  envVarId: str,
  version: z.number(),
  preview: nstr,
  valueFingerprint: str,
  byteLength: z.number(),
  lineCount: z.number(),
  note: nstr,
  current: z.boolean().optional(),
  pendingApprovalId: nstr,
  createdBy: UserSummary.nullish(),
  createdAt: str,
});
export type EnvVarVersion = z.infer<typeof EnvVarVersion>;
export const EnvVarVersionList = z.object({ data: z.array(EnvVarVersion), nextCursor: z.string().nullable() });

export const EnvVar = z.object({
  id: str,
  key: str,
  scope: z.enum(['org', 'project', 'app']),
  projectId: nstr,
  appId: nstr,
  environmentId: nstr,
  environment: nstr,
  platform: nstr,
  visibility: EnvVisibility,
  valueType: EnvValueType.catch('string'),
  configTime: z.boolean().optional(),
  description: nstr,
  currentVersion: EnvVarVersion.nullish(),
  inherited: z.boolean().optional(),
  createdBy: nstr,
  createdAt: str,
  updatedAt: str,
  etag: str.optional(),
});
export type EnvVar = z.infer<typeof EnvVar>;
export const EnvVarList = z.object({ data: z.array(EnvVar), nextCursor: z.string().nullable() });

export const EnvVarWriteResult = z.object({ envVar: EnvVar, version: EnvVarVersion, approval: z.object({ id: str, status: str }).nullish(), warnings: z.array(Warning).default([]) });
export type EnvVarWriteResult = z.infer<typeof EnvVarWriteResult>;

export const EnvVarRevealResponse = z.object({ envVarId: str, key: str, versionId: str, version: z.number(), visibility: z.enum(['plain', 'sensitive']), valueType: EnvValueType.catch('string'), value: str.optional(), valueBase64: str.optional(), revealedAt: str });
export type EnvVarRevealResponse = z.infer<typeof EnvVarRevealResponse>;

export const EnvImportItem = z.object({
  key: str,
  action: z.enum(['create', 'new_version', 'unchanged', 'skip', 'error']).catch('error'),
  visibility: EnvVisibility.optional(),
  valueType: EnvValueType.optional(),
  byteLength: nint,
  lineCount: nint,
  valueFingerprint: nstr,
  envVarId: nstr,
  warnings: z.array(Warning).default([]),
});
export type EnvImportItem = z.infer<typeof EnvImportItem>;
export const EnvImportResult = z.object({
  dryRun: z.boolean(),
  summary: z.object({ created: z.number(), newVersions: z.number(), unchanged: z.number(), skipped: z.number(), errors: z.number() }),
  items: z.array(EnvImportItem),
  warnings: z.array(Warning).default([]),
});
export type EnvImportResult = z.infer<typeof EnvImportResult>;

export const DefinedIn = z.enum(['ui', 'config_file']);
export const EnvRequirement = z.object({
  id: str.optional(),
  key: str,
  required: z.boolean().default(true),
  pattern: nstr,
  environments: z.array(str).nullish(),
  platforms: z.array(Platform).nullish(),
  expectedVisibility: EnvVisibility.nullish(),
  description: nstr,
  definedIn: DefinedIn.catch('ui'),
});
export type EnvRequirement = z.infer<typeof EnvRequirement>;
export const EnvRequirements = z.object({ appId: str.optional(), requirements: z.array(EnvRequirement), etag: str.optional() });
export type EnvRequirements = z.infer<typeof EnvRequirements>;
